import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Task } from "@/api.types";
import { useTrips } from "@/api/trips";
import { useTasks } from "@/api/tasks";
import { useUpdateTaskStatus } from "@/api/tasks";
import { useAssignTasksToTrip } from "@/api/tasks";
import { useUnassignTasksFromTrip } from "@/api/tasks";
import { CreateTaskDialog } from "./create-task-dialog";
import { Input } from "@/components/ui/input";

const statusColors = {
  PENDING: "bg-yellow-500",
  IN_PROGRESS: "bg-blue-500",
  COMPLETED: "bg-green-500",
  FAILED: "bg-red-500",
  CANCELLED: "bg-gray-500",
};

const typeColors = {
  PICKUP: "bg-blue-500",
  DELIVERY: "bg-purple-500",
  RETURN: "bg-yellow-500",
  DEPOT_RECEIVE: "bg-gray-500",
  DEPOT_SORT: "bg-gray-500",
  DEPOT_DISPATCH: "bg-gray-500",
};

interface SimpleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function SimpleDialog({ isOpen, onClose, children }: SimpleDialogProps) {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        {children}
      </div>
    </>
  );
}

export function TasksTable() {
  const { toast } = useToast();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);

  // Pagination and filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [tripFilter, setTripFilter] = useState<string | null>(null);

  // Use the paginated tasks API
  const {
    data: tasksResponse,
    isLoading,
    refetch,
  } = useTasks({
    page,
    limit,
    status: statusFilter,
    type: typeFilter,
    search: searchTerm || undefined,
    tripId: tripFilter,
  });

  // Extract tasks and metadata
  const tasks = tasksResponse?.data ?? [];
  const metadata = tasksResponse?.metadata;

  const { data: availableTrips } = useTrips();

  const updateTaskStatus = useUpdateTaskStatus({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Task status updated successfully",
      });
      void refetch();
    },
    onError: (error) => {
      console.error("Error updating task status:", error);
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
    },
  });

  const assignTaskToTrip = useAssignTasksToTrip({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Task assigned to trip successfully",
      });
      setIsAssignDialogOpen(false);
      setSelectedTaskId(null);
      setSelectedTripId(null);
      void refetch();
    },
    onError: (error) => {
      console.error("Error assigning task to trip:", error);
      toast({
        title: "Error",
        description: "Failed to assign task to trip",
        variant: "destructive",
      });
    },
  });

  const unassignTaskFromTrip = useUnassignTasksFromTrip({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Task unassigned from trip successfully",
      });
      void refetch();
    },
    onError: (error) => {
      console.error("Error unassigning task from trip:", error);
      toast({
        title: "Error",
        description: "Failed to unassign task from trip",
        variant: "destructive",
      });
    },
  });

  const handleUpdateStatus = (taskId: string, newStatus: Task["status"]) => {
    updateTaskStatus.mutate({ taskId, status: newStatus });
  };

  const handleAssignToTrip = (taskId: string, tripId: string) => {
    // Remove focus from any active element before proceeding
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    assignTaskToTrip.mutate({ tripId, taskIds: [taskId] });
  };

  const handleUnassignFromTrip = (taskId: string) => {
    const task = tasks?.find((t) => t.id === taskId);
    if (!task?.trip?.id) {
      toast({
        title: "Error",
        description: "Task is not assigned to a trip",
        variant: "destructive",
      });
      return;
    }
    unassignTaskFromTrip.mutate({ tripId: task.trip.id, taskIds: [taskId] });
  };

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on new search
    void refetch();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <SimpleDialog
        isOpen={isAssignDialogOpen}
        onClose={() => {
          setIsAssignDialogOpen(false);
          setSelectedTaskId(null);
          setSelectedTripId(null);
        }}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Select Trip</h2>
            <p className="text-sm text-gray-500">
              Choose a trip to assign this task to.
            </p>
          </div>

          <Select
            onValueChange={(tripId) => setSelectedTripId(tripId)}
            value={selectedTripId ?? undefined}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a trip" />
            </SelectTrigger>
            <SelectContent>
              {availableTrips?.data.map((trip) => (
                <SelectItem key={trip.id} value={trip.id}>
                  {`Trip #${trip.tripNumber}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAssignDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (selectedTaskId && selectedTripId) {
                  void handleAssignToTrip(selectedTaskId, selectedTripId);
                }
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </SimpleDialog>

      <div className="flex justify-between mb-4 flex-wrap gap-4">
        <div>
          <CreateTaskDialog onCreated={() => refetch()} />
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex">
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-r-none w-[200px]"
            />
            <Button type="submit" className="rounded-l-none">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          {/* Status Filter */}
          <Select
            value={statusFilter ?? "all"}
            onValueChange={(value) => {
              setStatusFilter(value === "all" ? undefined : value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select
            value={typeFilter ?? "all"}
            onValueChange={(value) => {
              setTypeFilter(value === "all" ? undefined : value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="PICKUP">Pickup</SelectItem>
              <SelectItem value="DELIVERY">Delivery</SelectItem>
              <SelectItem value="DEPOT_RECEIVE">Depot Receive</SelectItem>
              <SelectItem value="DEPOT_SORT">Depot Sort</SelectItem>
              <SelectItem value="DEPOT_DISPATCH">Depot Dispatch</SelectItem>
              <SelectItem value="RETURN">Return</SelectItem>
            </SelectContent>
          </Select>

          {/* Trip Filter */}
          <Select
            value={
              tripFilter === null
                ? "all"
                : tripFilter === "null"
                  ? "unassigned"
                  : "assigned"
            }
            onValueChange={(value) => {
              setTripFilter(
                value === "all"
                  ? null
                  : value === "unassigned"
                    ? "null"
                    : "assigned"
              );
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Trip Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>

          {/* Results per page selector */}
          <Select
            value={limit.toString()}
            onValueChange={(value) => {
              setLimit(parseInt(value));
              setPage(1); // Reset to first page when changing the limit
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Per page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 per page</SelectItem>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-white z-10 min-w-[150px]">
                Task Details
              </TableHead>
              <TableHead className="min-w-[120px]">Type</TableHead>
              <TableHead className="min-w-[120px]">Status</TableHead>
              <TableHead className="min-w-[150px]">Order</TableHead>
              <TableHead className="min-w-[200px]">Trip</TableHead>
              <TableHead className="min-w-[200px]">Operator</TableHead>
              <TableHead className="min-w-[150px]">Dependencies</TableHead>
              <TableHead className="min-w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length > 0 ? (
              tasks.map((task: Task) => (
                <TableRow key={task.id}>
                  <TableCell className="sticky left-0 bg-white">
                    <div>
                      <p className="font-medium">#{task.taskNumber}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(task.createdAt).toLocaleDateString()}
                      </p>
                      {task.description && (
                        <p className="text-sm text-gray-500">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`${typeColors[task.type]}`}
                      variant="default"
                    >
                      {task.type.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`${statusColors[task.status]}`}
                      variant="default"
                    >
                      {task.status.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">#{task.order?.orderNumber}</p>
                  </TableCell>
                  <TableCell>
                    {task.trip ? (
                      <div>
                        <p className="font-medium">
                          {`Trip #${task.trip.tripNumber}`}
                        </p>
                        {task.trip.vehicle && (
                          <p className="text-sm text-gray-500">
                            Vehicle: {task.trip.vehicle.plateNumber}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Unassigned</p>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.operator ? (
                      <p className="font-medium">
                        {`${task.operator.user.firstName} ${task.operator.user.lastName}`}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">Unassigned</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      {task.parentTask && (
                        <p className="text-sm text-gray-500">
                          Parent: #{task.parentTask.taskNumber}
                        </p>
                      )}
                      {task.childTasks?.length > 0 && (
                        <p className="text-sm text-gray-500">
                          Children: {task.childTasks.length}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {task.status === "PENDING" && (
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(task.id, "IN_PROGRESS")
                            }
                          >
                            Start Task
                          </DropdownMenuItem>
                        )}
                        {task.status === "IN_PROGRESS" && (
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(task.id, "COMPLETED")
                            }
                          >
                            Complete Task
                          </DropdownMenuItem>
                        )}
                        {!task.trip && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedTaskId(task.id);
                              setIsAssignDialogOpen(true);
                            }}
                          >
                            Assign to Trip
                          </DropdownMenuItem>
                        )}
                        {task.trip && (
                          <DropdownMenuItem
                            onClick={() => handleUnassignFromTrip(task.id)}
                          >
                            Unassign from Trip
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-6">
                  No tasks found matching the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {metadata && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-gray-500">
            Showing {metadata.total ? (page - 1) * limit + 1 : 0} to{" "}
            {Math.min(page * limit, metadata.total)} of {metadata.total} results
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= metadata.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
